import { useEffect, useState } from 'react';

import { breakpoints } from '@zeus/themes/next';

import { useMediaQueryContext } from '../../../context/MediaQueryContext';

export interface UseMediaQueryOptions {
  /**
   * The default value to return when the media query cannot be evaluated.
   * Used as fallback during SSR, when matchMedia is not available, or when noSsr is true.
   * @default false
   */
  defaultMatches?: boolean;

  /**
   * Custom matchMedia implementation to use instead of window.matchMedia.
   * Useful for testing or when running in environments without window.matchMedia.
   * Set to null to force using defaultMatches.
   * @default window.matchMedia (if available) or null
   */
  matchMedia?: typeof window.matchMedia | null;

  /**
   * When true, skips SSR entirely and always uses defaultMatches during initial render.
   * Prevents hydration mismatches by ensuring client and server render the same initial state.
   * The media query will be evaluated normally after the initial render.
   * @default false
   */
  noSsr?: boolean;

  /**
   * Custom function to evaluate media queries during server-side rendering.
   * Only used when window is undefined (SSR environment) and noSsr is false.
   * Receives the cleaned media query string (without @media prefix) and should return
   * an object with a matches boolean property.
   * @param query - The media query string to evaluate (e.g., "(min-width: 768px)")
   * @returns Object with matches property indicating if the query should match
   */
  ssrMatchMedia?: (query: string) => { matches: boolean };
}

export function useMediaQuery(
  query: string | ((breakpointsParam: typeof breakpoints) => string),
  {
    defaultMatches = false,
    matchMedia = typeof window !== 'undefined' ? window.matchMedia : null,
    noSsr = false,
    ssrMatchMedia,
  }: UseMediaQueryOptions = {}
): boolean {
  const { ssrMatchMedia: contextSsrMatchMedia } = useMediaQueryContext();
  const resolvedSsrMatchMedia = ssrMatchMedia || contextSsrMatchMedia;

  const queryString = typeof query === 'function' ? query(breakpoints) : query;
  const cleanQuery = queryString.replace(/^@media( ?)/m, '');

  const [matches, setMatches] = useState(() => {
    if (noSsr) {
      return defaultMatches;
    }

    if (typeof window === 'undefined') {
      if (resolvedSsrMatchMedia) {
        return resolvedSsrMatchMedia(cleanQuery).matches;
      }
      return defaultMatches;
    }

    if (!matchMedia) {
      return defaultMatches;
    }

    return matchMedia(cleanQuery).matches;
  });

  const handleChange = (event: MediaQueryListEvent) => {
    setMatches(event.matches);
  };

  useEffect(() => {
    if (!matchMedia) {
      // No matchMedia available, use defaultMatches
      setMatches(defaultMatches);
      return;
    }

    const mediaQuery = matchMedia(cleanQuery);
    setMatches(mediaQuery.matches);

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [cleanQuery, matchMedia, defaultMatches, resolvedSsrMatchMedia]);

  return matches;
}
